/**
 * usePermissions Hook
 * Provides permission checking utilities based on user role and permission overrides
 */

import { useMemo } from 'react'
import { useSession } from './useSession'
import type {
  ResourceType,
  Action,
  PermissionContext,
  PermissionCheck,
  Permission,
} from '../types/permissions'
import type { ProfileRole } from '../types'
import type { PermissionOverride } from '../types/agencyTeam'
import {
  getRolePermissions,
  ROLE_PERMISSIONS,
} from '../utils/permissions'
import {
  hasPermissionWithOverrides,
  getEffectivePermissions,
  getOverrideSummary,
} from '../api/permissionOverrides'

/**
 * Get user role from session
 */
function getUserRole(user: any): ProfileRole | undefined {
  // Check various possible role fields
  return user?.role || user?.profile?.role || user?.expand?.profile?.role || 'read only'
}

/**
 * Get permission overrides from user object
 */
function getPermissionOverrides(user: any): PermissionOverride[] | null {
  return user?.permission_overrides ?? null
}

export function usePermissions() {
  const { user } = useSession()
  const role = useMemo(() => getUserRole(user), [user])
  const userId = useMemo(() => user?.id, [user])
  const overrides = useMemo(() => getPermissionOverrides(user), [user])

  /**
   * Check if user has permission for resource/action (with overrides)
   */
  const checkPermission = useMemo(
    () => (resource: ResourceType, action: Action, context?: PermissionContext): PermissionCheck => {
      if (!role) return { allowed: false, reason: 'No role assigned' }

      // Check with overrides first
      const allowed = hasPermissionWithOverrides(role, overrides, resource, action)

      if (!allowed) {
        return {
          allowed: false,
          reason: `${role} role does not have ${action} permission for ${resource}`,
        }
      }

      // If there's a condition in the base permission, check it
      const basePermissions = ROLE_PERMISSIONS[role] || []
      const permission = basePermissions.find(
        p => p.resource === resource && (p.action === action || p.action === 'manage')
      )

      if (permission?.condition) {
        const conditionMet = permission.condition({ ...context, userId })
        if (!conditionMet) {
          return {
            allowed: false,
            reason: 'Condition check failed',
          }
        }
      }

      return { allowed: true }
    },
    [role, userId, overrides]
  )

  /**
   * Quick permission check (returns boolean) - includes overrides
   */
  const canDo = useMemo(
    () => (resource: ResourceType, action: Action, _context?: PermissionContext): boolean => {
      if (!role) return false
      return hasPermissionWithOverrides(role, overrides, resource, action)
    },
    [role, overrides]
  )

  /**
   * Check if user can manage a resource (includes overrides)
   */
  const canManageResource = useMemo(
    () => (resource: ResourceType): boolean => {
      if (!role) return false
      return hasPermissionWithOverrides(role, overrides, resource, 'manage')
    },
    [role, overrides]
  )

  /**
   * Get allowed actions for a resource (includes overrides)
   */
  const getAllowedActionsForResource = useMemo(
    () => (resource: ResourceType): Action[] => {
      if (!role) return []

      const actions: Action[] = ['create', 'read', 'update', 'delete', 'manage']
      return actions.filter(action =>
        hasPermissionWithOverrides(role, overrides, resource, action)
      )
    },
    [role, overrides]
  )

  /**
   * Get all effective permissions (role + overrides)
   */
  const permissions = useMemo((): Permission[] => {
    if (!role) return []
    return getEffectivePermissions(role, overrides)
  }, [role, overrides])

  /**
   * Get base role permissions (without overrides)
   */
  const basePermissions = useMemo((): Permission[] => {
    if (!role) return []
    return getRolePermissions(role)
  }, [role])

  /**
   * Get summary of permission overrides
   */
  const overrideSummary = useMemo(() => {
    if (!role) return { granted: [], revoked: [] }
    return getOverrideSummary(role, overrides)
  }, [role, overrides])

  /**
   * Check if user has any permission overrides
   */
  const hasOverrides = useMemo(() => {
    return overrides !== null && overrides.length > 0
  }, [overrides])

  return {
    // User info
    role,
    userId,

    // Permission checking
    checkPermission,
    can: canDo,
    canManage: canManageResource,
    getAllowedActions: getAllowedActionsForResource,

    // Permission data
    permissions,
    basePermissions,
    overrides,
    overrideSummary,
    hasOverrides,

    // Role checks
    isSuperAdmin: role === 'superadmin',
    isAdmin: role === 'admin' || role === 'superadmin',
    isAgent: role === 'agent',
    isAssistant: role === 'assistant',
    isReadOnly: role === 'read only',
  }
}
